import { AlgorithmResult, OptimizationResults, ParetoAnalysis, ParetoAnalysisResult } from '../types';

/**
 * Solution with algorithm and run identification
 * Pareto dominance is based on: energy (min), responseTime (min), cost (min)
 * Fitness is kept for reference but not used in dominance calculation
 */
export interface SolutionWithMetadata {
  algorithmName: string;
  algorithmKey: string;
  runId: number;
  energy: number;
  responseTime: number;
  cost: number;
  fitness?: number; // Optional: kept for reference but not used in dominance
  feasible?: boolean; // Optional feasibility flag
}

/**
 * Checks if a solution has valid (finite) values for Pareto objectives
 * Pareto objectives: energy (min), responseTime (min), cost (min)
 */
function isValidSolution(solution: SolutionWithMetadata): boolean {
  return (
    Number.isFinite(solution.energy) &&
    Number.isFinite(solution.responseTime) &&
    Number.isFinite(solution.cost)
  );
}

/**
 * Checks if solution A dominates solution B
 * A dominates B if:
 * - A.energy <= B.energy (minimize energy)
 * - A.responseTime <= B.responseTime (minimize response time)
 * - A.cost <= B.cost (minimize cost)
 * - At least one inequality is strict
 * 
 * Note: Fitness is not used in dominance as it's a scalar QoS score.
 * Pareto analysis focuses on the primary objectives: energy, response time, and cost.
 * 
 * Returns false if either solution has non-finite values (NaN/Infinity)
 */
export function dominates(a: SolutionWithMetadata, b: SolutionWithMetadata): boolean {
  // Guard: Skip comparison if either solution has non-finite values
  if (!isValidSolution(a) || !isValidSolution(b)) {
    return false;
  }
  
  const energyBetter = a.energy <= b.energy;
  const responseTimeBetter = a.responseTime <= b.responseTime;
  const costBetter = a.cost <= b.cost;
  
  const allBetterOrEqual = energyBetter && responseTimeBetter && costBetter;
  const atLeastOneStrict = 
    a.energy < b.energy || 
    a.responseTime < b.responseTime || 
    a.cost < b.cost;
  
  return allBetterOrEqual && atLeastOneStrict;
}

/**
 * Finds all Pareto-optimal solutions from a set of solutions
 * Filters out invalid (non-finite) and infeasible solutions before analysis
 * @param solutions - Array of solutions to analyze
 * @returns Array of Pareto-optimal solutions
 */
export function findParetoOptimal(solutions: SolutionWithMetadata[]): SolutionWithMetadata[] {
  if (solutions.length === 0) return [];
  
  // Filter out invalid and infeasible solutions
  const validSolutions = solutions.filter(sol => {
    // Skip if any value is not finite
    if (!isValidSolution(sol)) {
      return false;
    }
    // Skip if explicitly marked as infeasible (if feasibility flag is provided)
    if (sol.feasible === false) {
      return false;
    }
    return true;
  });
  
  if (validSolutions.length === 0) return [];
  
  const paretoOptimal: SolutionWithMetadata[] = [];
  
  for (const candidate of validSolutions) {
    let isDominated = false;
    
    for (const other of validSolutions) {
      if (candidate === other) continue;
      
      if (dominates(other, candidate)) {
        isDominated = true;
        break;
      }
    }
    
    if (!isDominated) {
      paretoOptimal.push(candidate);
    }
  }
  
  return paretoOptimal;
}

/**
 * Performs Pareto analysis across all algorithms and runs
 * @param allRuns - Array of results from all runs
 * @returns Pareto analysis results
 */
export function performParetoAnalysis(
  allRuns: OptimizationResults[]
): ParetoAnalysis {
  const algorithmKeys = ['ga', 'culture', 'frog', 'sflaga', 'sflaca', 'pso', 'gapso'] as const;
  const algorithmNames = ['GA', 'Culture', 'Frog', 'SFLAGA', 'SFLACA', 'PSO', 'GAPSO'];
  
  // Convert all runs to solutions with metadata
  const allSolutions: SolutionWithMetadata[] = [];
  
  allRuns.forEach((run, runId) => {
    algorithmKeys.forEach((algorithmKey, idx) => {
      const result = run[algorithmKey];
      if (result) {
        // CRITICAL: Filter out invalid solutions (NaN/Infinity) before Pareto analysis
        // Invalid values would corrupt dominance comparisons and Pareto front calculation
        // Pareto objectives: energy, responseTime, cost (fitness is optional for reference)
        if (!Number.isFinite(result.energy) || 
            !Number.isFinite(result.responseTime) || 
            !Number.isFinite(result.cost)) {
          return; // Skip this solution - not finite
        }
        
        // Optional: Check feasibility if available
        // (Currently AlgorithmResult doesn't have a feasible field,
        // but this structure supports it if added in the future)
        const feasible = (result as any).feasible !== false; // Default to true if not specified
        
        allSolutions.push({
          algorithmName: algorithmNames[idx],
          algorithmKey,
          runId,
          energy: result.energy,
          responseTime: result.responseTime,
          cost: result.cost,
          fitness: Number.isFinite(result.fitness) ? result.fitness : undefined, // Optional: kept for reference
          feasible, // Store feasibility flag for filtering in findParetoOptimal
        });
      }
    });
  });
  
  // Find global Pareto front
  const globalParetoFront = findParetoOptimal(allSolutions);
  
  // Count Pareto-optimal solutions per algorithm
  const algorithmCounts: { [key: string]: number } = {};
  algorithmKeys.forEach((key) => {
    algorithmCounts[key] = 0;
  });
  
  globalParetoFront.forEach((solution) => {
    algorithmCounts[solution.algorithmKey]++;
  });
  
  // Calculate percentage contribution per algorithm
  const totalParetoOptimal = globalParetoFront.length;
  const algorithmContributions: ParetoAnalysisResult[] = algorithmKeys.map((key, idx) => {
    const count = algorithmCounts[key];
    const percentage = totalParetoOptimal > 0 
      ? (count / totalParetoOptimal) * 100 
      : 0;
    
    return {
      algorithmName: algorithmNames[idx],
      paretoOptimalCount: count,
      percentageContribution: percentage,
    };
  });
  
  return {
    globalParetoFront: globalParetoFront.map((sol) => ({
      algorithmName: sol.algorithmName,
      runId: sol.runId,
      energy: sol.energy,
      responseTime: sol.responseTime,
      cost: sol.cost,
      fitness: sol.fitness, // Optional: kept for reference but not used in dominance
    })),
    algorithmContributions,
    totalSolutions: allSolutions.length,
    paretoOptimalSolutions: totalParetoOptimal,
  };
}

